"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _Platform = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Platform"));
var _Pressable = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Pressable"));
var _StyleSheet = _interopRequireDefault(require("react-native-web/dist/cjs/exports/StyleSheet"));
var _Text = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Text"));
var _View = _interopRequireDefault(require("react-native-web/dist/cjs/exports/View"));
var _shared = _interopRequireDefault(require("./shared"));
var _floatingUi = require("../utils/floating-ui");
var _ThemeProvider = require("../ThemeProvider");
var _utils = require("../utils");
var _TooltipButton = _interopRequireDefault(require("../TooltipButton"));
var _useCopy = _interopRequireDefault(require("../utils/useCopy"));
var _dictionary = _interopRequireDefault(require("./dictionary"));
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const [selectProps, selectedSystemPropTypes] = (0, _utils.selectSystemProps)([_utils.a11yProps, _utils.viewProps]);
const selectTooltipStyles = _ref => {
  let {
    backgroundColor,
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
    borderRadius
  } = _ref;
  return {
    backgroundColor,
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
    borderRadius
  };
};
const selectTooltipShadowStyles = _ref2 => {
  let {
    shadow,
    borderRadius
  } = _ref2;
  return {
    borderRadius,
    ...(0, _ThemeProvider.applyShadowToken)(shadow)
  };
};
const selectToolTipSizeStyles = _ref3 => {
  let {
    maxWidth,
    minWidth
  } = _ref3;
  return {
    maxWidth,
    minWidth
  };
};
const selectMobileTooltipPositionStyles = _ref4 => {
  let {
    top,
    left,
    position
  } = _ref4;
  return {
    top,
    left,
    position
  };
};
const selectMobileArrowStyles = (_ref5, _ref6) => {
  let {
    backgroundColor,
    arrowWidth,
    arrowBorderRadius,
    shadow
  } = _ref5;
  let {
    placement,
    x: arrowX,
    y: arrowY
  } = _ref6;
  const staticSide = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right'
  }[placement.split('-')[0]];
  return {
    backgroundColor,
    width: arrowWidth,
    height: arrowWidth,
    borderBottomRightRadius: arrowBorderRadius,
    // this corner will be the arrow tip after rotation
    transform: [{
      rotateZ: '45deg'
    }],
    ...(0, _ThemeProvider.applyShadowToken)(shadow),
    left: arrowX != null && arrowX,
    top: arrowY != null && arrowY,
    right: '',
    bottom: '',
    [staticSide]: '-4px'
  };
};
const selectTextStyles = tokens => (0, _ThemeProvider.applyTextStyles)((0, _utils.selectTokens)('Typography', tokens));
const defaultControl = (pressableState, variant) => /*#__PURE__*/(0, _jsxRuntime.jsx)(_TooltipButton.default, {
  pressableState: pressableState,
  variant: variant
});

/**
 * Tooltip provides a descriptive and detailed explanation or instructions. It can be used next to an input label
 * to help a user fill it in, or as a standalone component.
 *
 * By default the TooltipButton component will be used as a control for triggering the tooltip, but you may attach
 * a tooltip to any other component. A render function can be used to adjust the control's styling on state changes (hover, focus, etc.).
 *
 * ### Positioning
 * By default a Tooltip will be automatically positioned in a way that ensures it fits within the viewport.
 * You may suggest a position with a prop - it will be used, unless the tooltip would end up outside the viewport.
 *
 * ### Usage criteria
 * - You may use one when the information is useful only to a small percentage of users (ie. tech savvy people wouldn't need this info).
 * - Tooltips may also be useful when vertical space is an issue.
 */
const Tooltip = /*#__PURE__*/_react.default.forwardRef((_ref7, ref) => {
  let {
    children,
    content,
    position = 'auto',
    copy = 'en',
    onPress = () => {},
    tokens,
    variant,
    inline = false,
    nativeID,
    ...rest
  } = _ref7;
  const [isOpen, setIsOpen] = _react.default.useState(false);
  const arrowRef = _react.default.useRef();
  const [tooltipDimensions, setTooltipDimensions] = _react.default.useState(null);
  const positions = {
    auto: undefined,
    // Auto needs to specifically bet set to undefined
    above: 'top',
    right: 'right',
    below: 'bottom',
    left: 'left'
  };

  // "AutoPlacement" can't be used with "flip"
  const additionalMiddleware = position !== 'auto' ? _floatingUi.autoPlacement : _floatingUi.flip;
  const {
    x,
    y,
    reference,
    floating,
    strategy,
    placement,
    middlewareData: {
      arrow: arrowData = {}
    }
  } = (0, _floatingUi.useFloating)({
    placement: positions[position],
    middleware: [(0, _floatingUi.shift)(), additionalMiddleware(), (0, _floatingUi.offset)(15), (0, _floatingUi.arrow)({
      element: arrowRef
    })]
  });
  const getCopy = (0, _useCopy.default)({
    dictionary: _dictionary.default,
    copy
  });
  const themeTokens = (0, _ThemeProvider.useThemeTokens)('Tooltip', tokens, variant);
  const toggleIsOpen = () => {
    onPress();
    setIsOpen(!isOpen);
  };
  const close = () => setIsOpen(false);
  const getPressableState = _ref8 => {
    let {
      pressed,
      hovered,
      focused
    } = _ref8;
    return {
      pressed,
      hover: hovered,
      focus: focused
    };
  };
  const onTooltipLayout = _ref9 => {
    let {
      nativeEvent: {
        layout: {
          width,
          height
        }
      }
    } = _ref9;
    if (tooltipDimensions === null || tooltipDimensions.width !== width || tooltipDimensions.height !== height) {
      setTooltipDimensions({
        width: _Platform.default.select({
          web: width + 0.3,
          // avoids often unnecessary line breaks due to subpixel rendering of fonts
          native: width
        }),
        height
      });
    }
  };
  _react.default.useEffect(() => {
    if (!isOpen) {
      setTooltipDimensions(null);
    }
  }, [isOpen]);
  const control = children !== undefined ? children : defaultControl;
  const pressableStyles = control === defaultControl ? _Platform.default.select({
    web: {
      outline: 'none'
    }
  }) : undefined;
  const pressableHitSlop = control === defaultControl ? {
    top: 10,
    bottom: 10,
    left: 10,
    right: 10
  } : undefined;
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_View.default, {
    style: [staticStyles.container, _Platform.default.select({
      web: {
        display: inline ? 'inline-block' : 'flex'
      }
    })],
    ...selectProps(rest),
    ref: ref,
    children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_Pressable.default, {
      onPress: toggleIsOpen,
      ref: reference,
      onBlur: close,
      style: pressableStyles,
      hitSlop: pressableHitSlop,
      accessibilityLabel: getCopy('a11yText'),
      accessibilityRole: "button",
      nativeID: nativeID,
      children: typeof control === 'function' ? pressableState => control(getPressableState(pressableState), variant) : control
    }), isOpen && /*#__PURE__*/(0, _jsxRuntime.jsxs)(_View.default, {
      ref: floating,
      style: [selectTooltipShadowStyles(themeTokens), staticStyles.tooltip,
      // applied separately so that it doesn't cover the arrow
      selectToolTipSizeStyles, selectMobileTooltipPositionStyles({
        position: strategy,
        top: y ?? 0,
        left: x ?? 0
      })],
      onLayout: onTooltipLayout,
      accessibilityRole: "alert",
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_View.default, {
        style: [staticStyles.arrow, selectMobileArrowStyles(themeTokens, {
          ...arrowData,
          placement
        })],
        ref: arrowRef
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_View.default, {
        style: selectTooltipStyles(themeTokens),
        children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Text.default, {
          style: selectTextStyles(themeTokens),
          children: content
        })
      })]
    })]
  });
});
Tooltip.displayName = 'Tooltip';
Tooltip.propTypes = {
  ...selectedSystemPropTypes,
  ..._shared.default
};
var _default = exports.default = Tooltip;
const staticStyles = _StyleSheet.default.create({
  container: {
    alignItems: 'flex-start',
    position: 'absolute'
  },
  tooltip: {
    position: 'absolute',
    top: 0,
    left: 0
  },
  arrow: {
    position: 'absolute'
  }
});